/**
 * SKE-4549 — the browser's read of the campaign status endpoint.
 *
 * Through the shared generated openapi-fetch client, like
 * `features/grid-designer/api/grid-plans.ts`: same CSRF and 401 handling, no
 * hand-rolled fetch, no parallel client.
 *
 * Every campaign surface reads THIS. None of them derives the week, the row
 * state or whether a banner may be dismissed; the server answers all of it, and
 * four surfaces reading one answer is the whole design.
 */
import { GeneratedSchedugramServerClient } from "../../api/generated-schedugram-server-client";
import type { paths } from "../../api/generated/schedugram-server-openapi-types";

const client = new GeneratedSchedugramServerClient<paths>();

type StatusResponse =
  paths["/instagram/publishing-campaign"]["get"]["responses"][200]["content"]["application/json"];

export type CampaignState = StatusResponse["campaign"];
export type CampaignAccountRow = StatusResponse["accounts"][number];
/**
 * One Facebook Page row (SKE-4693).
 *
 * A SECOND GROUP, never the same shape as an Instagram row. It carries no
 * `blockers`, no `apiPublishable` and no `rowState`, because a Page has no
 * deadline and is never counted. The contract withholds those fields on
 * purpose, so nothing in the browser can total them up or paint one red.
 */
export type CampaignPageRow = StatusResponse["pages"][number];
export type CampaignStatus = StatusResponse;

export async function getInstagramCampaignStatus(
  campaignWeek?: string
): Promise<CampaignStatus> {
  const result = await client.client.GET("/instagram/publishing-campaign", {
    params: { query: campaignWeek ? { campaignWeek } : {} },
  });
  return client.unwrap(result);
}

/* ---------------------------------------------------- SKE-4687, the pass */

type AuthorisePath = paths["/instagram/publishing-campaign/authorise"]["post"];

/**
 * The Facebook credential, and nothing else.
 *
 * There is deliberately no account id in this body: the estate is resolved
 * from the signed-in workspace on the server, so a caller cannot name an
 * account they do not own.
 */
export type CampaignAuthoriseBody =
  AuthorisePath["requestBody"]["content"]["application/json"];
export type CampaignAuthoriseResult =
  AuthorisePath["responses"][200]["content"]["application/json"];
export type CampaignAuthoriseRow = CampaignAuthoriseResult["rows"][number];
export type CampaignAuthorisePageRow = CampaignAuthoriseResult["pages"][number];

/**
 * Runs one authorisation pass over every account in this workspace.
 *
 * ONE REQUEST AND ONE RESPONSE, with no progress channel: a fifty-account
 * agency makes fifty sequential Graph calls inside it, which is about a
 * minute of silence. The page polls the status endpoint while this is in
 * flight so rows flip in place; see `useCampaignStatus`'s `isPassRunning`.
 */
export async function authoriseInstagramCampaign(
  body: CampaignAuthoriseBody
): Promise<CampaignAuthoriseResult> {
  const result = await client.client.POST(
    "/instagram/publishing-campaign/authorise",
    { body }
  );
  return client.unwrap(result);
}

/* ------------------------------------- SKE-4579, the code-grant exchange */

export { exchangeFacebookBusinessLoginCode } from "../../api/social-account-connect/facebook/code-exchange";
export type {
  FacebookBusinessLoginCodeExchangeBody,
  FacebookBusinessLoginCodeExchangeResult,
} from "../../api/social-account-connect/facebook/code-exchange";
